import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { BookMarked, Loader2 } from 'lucide-react';
import api from '@/utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGoogleLogin = async (retryCount = 0) => {
    setLoading(true);
    setError(null);
    
    try {
      // Get Google OAuth URL from backend
      const response = await api.get('/api/auth/google/login');
      const authUrl = response.data.auth_url;
      
      if (!authUrl) {
        throw new Error('URL de autenticação não recebida');
      }
      
      // Redirect to Google
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to get Google auth URL:', err);
      
      // Retry up to 2 times
      if (retryCount < 2) {
        console.log(`Retrying... attempt ${retryCount + 2}`);
        setTimeout(() => handleGoogleLogin(retryCount + 1), 1000);
        return;
      }
      
      setLoading(false);
      setError('Erro ao conectar com Google. Verifique sua conexão e tente novamente.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Background Image - Mobile */}
      <div 
        className="lg:hidden w-full aspect-square max-h-[350px] bg-cover bg-center relative"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1631816290138-9f0f79cada3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxibGFjayUyMHdvbWFuJTIwcmVzZWFyY2hlciUyMHN0dWR5aW5nJTIwbGFib3JhdG9yeSUyMG5vdGVib29rfGVufDB8fHx8MTc3NDU5NDIyMHww&ixlib=rb-4.1.0&q=85')",
          backgroundPosition: "center 20%"
        }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#002FA7] to-transparent" />
      </div>

      {/* Left Side - Image (Desktop) */}
      <div className="hidden lg:block lg:w-1/2 auth-background" />

      {/* Right Side - Form */}
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center justify-center h-14 w-14 rounded-xl bg-[#002FA7]/10 border border-[#002FA7]/20">
              <BookMarked className="h-8 w-8 text-[#002FA7]" strokeWidth={1.5} />
            </div>
            <h1 className="text-3xl font-bold tracking-tighter text-[#002FA7]">
              Quero Publicar
            </h1>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight mb-2 text-gray-900">
              Bem-vinda(o)!
            </h2>
            <p className="text-gray-600">
              Plataforma de Chamadas para Publicações Científicas
            </p>
          </div>

          {/* Google Login Button */}
          <Button
            data-testid="google-login-btn"
            onClick={() => handleGoogleLogin(0)}
            disabled={loading}
            className="w-full bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 py-6 flex items-center justify-center gap-3 shadow-sm"
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-base font-medium">Conectando...</span>
              </>
            ) : (
              <>
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-base font-medium">Entrar com Google gratuitamente</span>
              </>
            )}
          </Button>
          
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
              <button 
                onClick={() => handleGoogleLogin(0)}
                className="block mt-2 text-red-800 underline font-medium"
              >
                Tentar novamente
              </button>
            </div>
          )}

          <p className="mt-6 text-sm text-gray-600">
            Ao entrar, você concorda com nossos{' '}
            <a href="/terms" className="text-[#002FA7] hover:underline">Termos de Uso</a>
            {' '}e{' '}
            <a href="/privacy" className="text-[#002FA7] hover:underline">Política de Privacidade</a>
          </p>

          {/* Info Box */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg text-left">
            <p className="text-sm text-blue-900">
              <strong>🔒 Login Seguro</strong>
              <br />
              Utilizamos autenticação via Google para garantir a segurança da sua conta.
              Após o login, você completará seu perfil acadêmico.
            </p>
          </div>

          {/* Features */}
          <div className="mt-8 grid grid-cols-2 gap-4 text-left">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium">📚 Chamadas QUALIS</p>
              <p className="text-xs text-gray-500">Filtros A1 até C</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium">🤖 Busca com IA</p>
              <p className="text-xs text-gray-500">Encontre por resumo</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium">🔔 Lembretes</p>
              <p className="text-xs text-gray-500">Email antes do prazo</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium">⭐ Favoritos</p>
              <p className="text-xs text-gray-500">Salve chamadas</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
